import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface EmailUpdateFormProps {
  userId: string;
  userName: string;
  currentEmail: string;
  onSubmit: () => void;
}

export function EmailUpdateForm({ userId, userName, currentEmail, onSubmit }: EmailUpdateFormProps) {
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!newEmail || !confirmEmail) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all fields.",
      });
      return;
    }

    if (!validateEmail(newEmail)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    if (newEmail !== confirmEmail) {
      toast({
        variant: "destructive",
        title: "Email Mismatch",
        description: "Email addresses do not match.",
      });
      return;
    }

    if (newEmail === currentEmail) {
      toast({
        variant: "destructive",
        title: "No Change",
        description: "New email is the same as current email.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the edge function to update email
      const { data, error } = await supabase.functions.invoke('update-user-email', {
        body: { userId, newEmail }
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Email Updated",
        description: `Email successfully updated for ${userName}. User must log in with new email.`,
      });

      setNewEmail('');
      setConfirmEmail('');
      onSubmit();
    } catch (error: any) {
      console.error('Error updating email:', error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error.message || "Failed to update email. Email may already exist in system.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Organization/User</Label>
        <Input value={userName} disabled />
      </div>

      <div className="space-y-2">
        <Label>Current Email</Label>
        <Input value={currentEmail} disabled />
      </div>

      <div className="space-y-2">
        <Label htmlFor="newEmail">New Email</Label>
        <Input
          id="newEmail"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Enter new email address"
          disabled={isSubmitting}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmEmail">Confirm New Email</Label>
        <Input
          id="confirmEmail"
          type="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder="Confirm new email address"
          disabled={isSubmitting}
          required
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Updating..." : "Update Email"}
        </Button>
      </div>
    </form>
  );
}
